import React, { useState, useEffect, useMemo } from "react";
import { Modal, ModalBody, Badge, Button, Spinner, Card, CardBody, Input } from "reactstrap";
import { Tooltip } from "antd";
import GeneralButton from "../button/GeneralButton";
import toast from "react-hot-toast";
import { bulkHumanizeProductDescriptions } from "../../api/products.api";
import { formatAIResponseToHTML } from "../../firebase/utility";
import { IoCloseSharp } from "react-icons/io5";
import { 
  FiLayers, 
  FiPackage, 
  FiInfo, 
  FiCheckCircle, 
  FiArrowLeft, 
  FiEdit3, 
  FiRotateCcw,
  FiZap,
  FiEye,
  FiList
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Separator from "../Separator";

const cleanMPN = (text) => {
  if (!text || typeof text !== "string") return text || "";
  return text
    .replace(/,\s*MPN\s+[a-zA-Z0-9_-]+,?\s*/gi, ", ")
    .replace(/\bMPN\s*[:#-]?\s*[a-zA-Z0-9_-]+\b,?\s*/gi, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/,\s*,/g, ",")
    .trim();
};

const prepareMarkdownForHTML = (text) => {
  if (!text || typeof text !== "string") return text || "";
  return text
    .replace(/([^\n])\s*(#{1,4}\s+)/g, "$1\n\n$2")
    .replace(/([^\n])\s*•\s*/g, "$1\n- ")
    .replace(/([^\n])\s+-\s+/g, "$1\n- ");
};

const extractBulkHumanizeResults = (res, parsedIdentifiers = [], isGroups = false) => {
  console.log("[BulkHumanize] API Response:", res);
  if (!res) return [];

  const body = res?.data || res;
  let rawData = body?.data ?? body?.results ?? body?.items ?? body?.updates ?? body?.preview ?? body?.products ?? body?.groups ?? body;

  // If rawData is wrapped in another layer like { data: { updates: [...] } } or { updates: [...] }
  if (rawData && typeof rawData === "object" && !Array.isArray(rawData)) {
    if (Array.isArray(rawData.updates)) rawData = rawData.updates;
    else if (Array.isArray(rawData.preview)) rawData = rawData.preview;
    else if (Array.isArray(rawData.results)) rawData = rawData.results;
    else if (Array.isArray(rawData.items)) rawData = rawData.items;
    else if (Array.isArray(rawData.products)) rawData = rawData.products;
    else if (Array.isArray(rawData.groups)) rawData = rawData.groups;
    else if (Array.isArray(rawData.data)) rawData = rawData.data;
  }

  const parseItem = (item, keyFallback, idxFallback) => {
    if (Array.isArray(item)) item = item[0] || {};
    const subObj = (item && typeof item === "object") 
      ? (item.generated || item.humanized || item.data || item.update || item) 
      : {};

    const extractIdent = () => {
      const candidates = [
        item?.sku,
        item?.SKU,
        item?.productSku,
        item?.product_sku,
        item?.item_sku,
        item?.itemSku,
        item?.slug,
        item?.groupSlug,
        item?.group_slug,
        item?.identifier,
        subObj?.sku,
        subObj?.SKU,
        subObj?.productSku,
        subObj?.product_sku,
        subObj?.item_sku,
        subObj?.itemSku,
        subObj?.slug,
        subObj?.groupSlug,
        subObj?.group_slug,
        subObj?.identifier,
        keyFallback,
      ];
      for (const c of candidates) {
        if (c && typeof c === "string" && c.trim()) return c.trim();
      }
      return parsedIdentifiers[idxFallback] || `Item #${idxFallback + 1}`;
    };

    const identifier = extractIdent();

    const name = 
      item?.name || 
      item?.title || 
      item?.item_title || 
      item?.product_name || 
      item?.group_name || 
      subObj?.name || 
      subObj?.title || 
      subObj?.item_title || 
      subObj?.product_name || 
      subObj?.group_name || 
      identifier;

    const rawShort = 
      subObj?.description ?? 
      subObj?.short_description ?? 
      subObj?.shortDescription ?? 
      subObj?.shortDesc ?? 
      subObj?.short ?? 
      subObj?.summary ?? 
      item?.description ?? 
      item?.short_description ?? 
      item?.shortDescription ?? 
      item?.shortDesc ?? 
      item?.summary ?? 
      "";

    const rawLong = 
      subObj?.long_description ?? 
      subObj?.longDescription ?? 
      subObj?.longDesc ?? 
      subObj?.overview ?? 
      subObj?.long ?? 
      subObj?.details ?? 
      subObj?.content ?? 
      subObj?.html ?? 
      subObj?.ai_description ?? 
      subObj?.humanizedDescription ?? 
      subObj?.humanized_long_description ?? 
      subObj?.body ?? 
      (typeof subObj === "string" ? subObj : "") ?? 
      item?.long_description ?? 
      item?.longDescription ?? 
      item?.longDesc ?? 
      item?.overview ?? 
      item?.details ?? 
      (typeof item === "string" ? item : "") ?? 
      "";

    const cleanedShort = cleanMPN(typeof rawShort === "string" ? rawShort : String(rawShort || ""));
    const preparedLong = prepareMarkdownForHTML(typeof rawLong === "string" ? rawLong : String(rawLong || ""));
    const formattedLong = (formatAIResponseToHTML && preparedLong) ? formatAIResponseToHTML(preparedLong) : preparedLong;

    return {
      identifier,
      name,
      description: cleanedShort,
      long_description: formattedLong,
      originalDescription: cleanedShort,
      originalLongDescription: formattedLong,
    };
  };

  let list = [];
  if (Array.isArray(rawData)) {
    list = rawData.map((item, idx) => parseItem(item, null, idx));
  } else if (rawData && typeof rawData === "object") {
    list = Object.entries(rawData).map(([key, val], idx) => parseItem(val, key, idx));
  }

  // Filter out non-data keys
  list = list.filter((item) => item.identifier !== "message" && item.identifier !== "status" && item.identifier !== "success");

  // Re-order strictly according to parsedIdentifiers if possible to prevent shuffling
  if (Array.isArray(parsedIdentifiers) && parsedIdentifiers.length > 0) {
    const orderedList = [];
    const remaining = [...list];

    parsedIdentifiers.forEach((targetId) => {
      const normTarget = String(targetId).trim().toLowerCase();
      const matchIdx = remaining.findIndex((item) => String(item.identifier).trim().toLowerCase() === normTarget);
      if (matchIdx !== -1) {
        orderedList.push(remaining[matchIdx]);
        remaining.splice(matchIdx, 1);
      }
    });

    // Append any extra results that weren't in parsedIdentifiers
    return [...orderedList, ...remaining];
  }

  return list;
};

const BulkHumanizeModal = ({ 
  isOpen, 
  toggle, 
  targetType = "groups", // 'groups' | 'products'
  initialItems = [], 
  refreshData 
}) => {
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [directLoading, setDirectLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  const [previewItems, setPreviewItems] = useState([]);
  const [activeItemIdx, setActiveItemIdx] = useState(0);

  // Sync initial items when modal opens
  useEffect(() => {
    if (isOpen) {
      if (Array.isArray(initialItems) && initialItems.length > 0) {
        const textLines = initialItems
          .map((item) => {
            if (typeof item === "string") return item.trim();
            if (targetType === "groups") {
              return (item.slug || item.groupSlug || item.name || item._id || "").trim();
            }
            return (item.sku || item.item_sku || item.name || item._id || "").trim();
          })
          .filter(Boolean);
        setInputText(textLines.join("\n"));
      } else {
        setInputText("");
      }
      setIsPreviewMode(false);
      setPreviewItems([]);
      setActiveItemIdx(0);
    }
  }, [isOpen, initialItems, targetType]);

  const resetState = () => {
    setInputText("");
    setLoading(false);
    setDirectLoading(false);
    setApplying(false);
    setIsPreviewMode(false);
    setPreviewItems([]);
    setActiveItemIdx(0);
  };

  const handleToggle = () => {
    resetState();
    toggle();
  };

  // Parse multiline textarea input by line-breaks (\n)
  const parsedIdentifiers = useMemo(() => {
    if (!inputText) return [];
    return inputText
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }, [inputText]);

  const isGroups = targetType === "groups";

  // ACTION A: Preview Only (preview: true)
  const handleGeneratePreview = () => {
    if (parsedIdentifiers.length === 0) {
      toast.error(`Please enter at least one ${isGroups ? "group slug" : "product SKU"}.`);
      return;
    }

    setLoading(true);
    const payload = isGroups
      ? {
          targetType: "groups",
          slugs: parsedIdentifiers,
          preview: true,
        }
      : {
          targetType: "products",
          skus: parsedIdentifiers,
          preview: true,
        };

    bulkHumanizeProductDescriptions(payload, (res) => {
      setLoading(false);
      if (res?.status === 200 || res?.success) {
        const normalized = extractBulkHumanizeResults(res, parsedIdentifiers, isGroups);

        if (normalized.length === 0) {
          toast.error("No descriptions were returned. Please verify the slugs/SKUs.");
          return;
        }

        setPreviewItems(normalized);
        setActiveItemIdx(0);
        setIsPreviewMode(true);
        toast.success(`Generated preview for ${normalized.length} ${isGroups ? "groups" : "products"}!`);
      } else {
        toast.error(res?.message || "Failed to generate preview descriptions.");
      }
    });
  };

  // ACTION B: Direct Generate & Update (Bypass Preview)
  const handleDirectGenerateAndUpdate = () => {
    if (parsedIdentifiers.length === 0) {
      toast.error(`Please enter at least one ${isGroups ? "group slug" : "product SKU"}.`);
      return;
    }

    setDirectLoading(true);
    const payload = isGroups
      ? {
          targetType: "groups",
          slugs: parsedIdentifiers,
        }
      : {
          targetType: "products",
          skus: parsedIdentifiers,
        };

    bulkHumanizeProductDescriptions(payload, (res) => {
      setDirectLoading(false);
      if (res?.status === 200 || res?.success) {
        toast.success(res?.data?.message || `Successfully generated and updated ${parsedIdentifiers.length} ${isGroups ? "groups" : "products"}!`);
        if (refreshData) refreshData();
        resetState();
      } else {
        toast.error(res?.message || "Failed to direct-generate and update descriptions.");
      }
    });
  };

  // ACTION C: Apply Edited Preview to DB (updates: [...])
  const handleApplyPreviewToDB = () => {
    if (previewItems.length === 0) {
      toast.error("No preview items available to apply.");
      return;
    }

    setApplying(true);
    const updates = previewItems.map((item) => {
      if (isGroups) {
        return {
          slug: item.identifier,
          description: item.description,
          long_description: item.long_description,
        };
      }
      return {
        sku: item.identifier,
        description: item.description,
        long_description: item.long_description,
      };
    });

    const payload = {
      targetType: isGroups ? "groups" : "products",
      updates,
    };

    bulkHumanizeProductDescriptions(payload, (res) => {
      setApplying(false);
      if (res?.status === 200 || res?.success) {
        toast.success(res?.data?.message || `Successfully updated ${updates.length} ${isGroups ? "groups" : "products"} in database!`);
        if (refreshData) refreshData();
        resetState();
      } else {
        toast.error(res?.message || "Failed to apply updates to database.");
      }
    });
  };

  // Inline editing helper for preview items - keyed safely by unique identifier
  const handleUpdateItem = (identifier, field, value) => {
    setPreviewItems((prev) =>
      prev.map((item) => {
        if (item.identifier === identifier) {
          return {
            ...item,
            [field]: value,
          };
        }
        return item;
      })
    );
  };

  const handleResetActiveItem = () => {
    if (!activeItem) return;
    setPreviewItems((prev) =>
      prev.map((item) => {
        if (item.identifier === activeItem.identifier) {
          return {
            ...item,
            description: item.originalDescription,
            long_description: item.originalLongDescription,
          };
        }
        return item;
      })
    );
    toast.success(`Reset ${activeItem.identifier} descriptions to AI original.`);
  };

  const activeItem = previewItems[activeItemIdx] || null;

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered size="xl" className="modal-dialog-scrollable">
      <ModalBody className="p-4 bg-white">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary-soft p-3 rounded-circle text-primary">
              {isGroups ? <FiLayers size={24} /> : <FiPackage size={24} />}
            </div>
            <div>
              <h4 className="primary-color fw-bold mb-0">
                {isGroups ? "Bulk AI Humanize Group Descriptions" : "Bulk AI Humanize Product Descriptions"}
              </h4>
              <p className="text-muted small mb-0 mt-1">
                {isGroups 
                  ? "Batch generate, preview, edit, and apply professional marketing descriptions for Variant Groups powered by AI."
                  : "Batch generate, preview, edit, and apply professional marketing descriptions for Individual Products powered by AI."}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-light btn-sm rounded-circle p-1 d-flex align-items-center justify-content-center text-muted"
            onClick={handleToggle}
            style={{ width: "32px", height: "32px" }}
          >
            <IoCloseSharp size={20} />
          </button>
        </div>
        <Separator />

        <div className="mt-3">
          {!isPreviewMode ? (
            /* ========================================================================= */
            /* STEP 1: LINE-BREAK TEXTAREA INPUT VIEW */
            /* ========================================================================= */
            <div className="p-4 border rounded-3 bg-light shadow-2xs">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="fw-bold text-dark small mb-0 d-flex align-items-center gap-2">
                  <FiList size={16} className="text-primary" />
                  {isGroups ? "Enter Group Slugs (one per line)" : "Enter Product SKUs (one per line)"}
                </label>
                <div className="d-flex align-items-center gap-2">
                  <Badge color={parsedIdentifiers.length > 0 ? "primary" : "secondary"} pill className="px-2 py-1">
                    {parsedIdentifiers.length} {isGroups ? "Group(s)" : "Product(s)"} detected
                  </Badge>
                  {parsedIdentifiers.length > 0 && (
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 text-danger small text-decoration-none"
                      onClick={() => setInputText("")}
                      disabled={loading || directLoading}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              <Input
                type="textarea"
                rows={8}
                className="font-monospace bg-white border-2"
                style={{ fontSize: "13px", resize: "vertical" }}
                placeholder={
                  isGroups
                    ? "lenovo-thinkpad-x1-carbon-gen-10\ndell-latitude-5530\nhp-elitebook-840-g9"
                    : "TMU-100234\nTMU-100235\nTMU-100236"
                }
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                disabled={loading || directLoading}
              />

              {/* Identifier Pills Preview */}
              {parsedIdentifiers.length > 0 && (
                <div className="mt-3 p-2 bg-white rounded border">
                  <div className="d-flex align-items-center gap-1 mb-2">
                    <FiInfo className="text-primary" size={13} />
                    <span className="fw-bold small text-dark">Detected Identifiers:</span>
                  </div>
                  <div className="d-flex flex-wrap gap-1" style={{ maxHeight: "90px", overflowY: "auto" }}>
                    {parsedIdentifiers.map((id, idx) => (
                      <span key={idx} className="badge bg-light text-dark border px-2 py-1 font-monospace" style={{ fontSize: "11px" }}>
                        {id}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top flex-wrap gap-2">
                <Button
                  color="outline-secondary"
                  className="d-flex align-items-center gap-2"
                  onClick={handleDirectGenerateAndUpdate}
                  disabled={loading || directLoading || parsedIdentifiers.length === 0}
                >
                  {directLoading ? <Spinner size="sm" /> : <FiZap size={16} />}
                  Direct Generate & Update (Bypass Preview)
                </Button>

                <GeneralButton
                  title={
                    loading
                      ? "Generating AI Preview..."
                      : `Generate & Preview Descriptions (${parsedIdentifiers.length})`
                  }
                  className="blue-button mb-0"
                  width="320px"
                  icon={<HiSparkles size={16} />}
                  loading={loading}
                  disabled={loading || directLoading || parsedIdentifiers.length === 0}
                  handleClick={handleGeneratePreview}
                />
              </div>
            </div>
          ) : (
            /* ========================================================================= */
            /* STEP 2: INTERACTIVE PREVIEW & INLINE EDITING VIEW (preview: true) */
            /* ========================================================================= */
            <div>
              {/* Header Bar */}
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 pb-2 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <Button
                    color="light"
                    size="sm"
                    className="border d-flex align-items-center gap-1"
                    onClick={() => setIsPreviewMode(false)}
                    disabled={applying}
                  >
                    <FiArrowLeft size={14} /> Back to Edit List
                  </Button>
                  <span className="text-muted small">
                    Generated preview for <strong className="text-dark">{previewItems.length}</strong> {isGroups ? "groups" : "products"}.
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <Button
                    color="outline-secondary"
                    size="sm"
                    className="d-flex align-items-center gap-1"
                    onClick={handleResetActiveItem}
                    disabled={applying || !activeItem}
                  >
                    <FiRotateCcw size={13} /> Reset Active to AI Original
                  </Button>
                </div>
              </div>

              {/* 2-Column Split: Items Navigation Sidebar & Active Item Editor */}
              <div className="row g-3">
                {/* Left Sidebar: List of items */}
                <div className="col-md-4">
                  <div className="border rounded bg-light p-2" style={{ maxHeight: "500px", overflowY: "auto" }}>
                    <label className="fw-bold small text-muted px-2 mb-2">
                      Select {isGroups ? "Group" : "Product"} to Review:
                    </label>
                    <div className="d-flex flex-column gap-1">
                      {previewItems.map((item, idx) => {
                        const isSelected = idx === activeItemIdx;
                        const isModified = 
                          item.description !== item.originalDescription || 
                          item.long_description !== item.originalLongDescription;

                        return (
                          <div
                            key={idx}
                            onClick={() => setActiveItemIdx(idx)}
                            className={`p-2 rounded-2 pointer transition-all border d-flex justify-content-between align-items-center ${
                              isSelected
                                ? "bg-primary text-white border-primary shadow-sm"
                                : "bg-white text-dark border-light-subtle hover-bg-light"
                            }`}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="text-truncate me-2" style={{ maxWidth: "200px" }}>
                              <div className={`fw-bold font-monospace small ${isSelected ? "text-white" : "text-primary"}`}>
                                {item.identifier}
                              </div>
                              <div className={`small text-truncate ${isSelected ? "text-white-50" : "text-muted"}`} style={{ fontSize: "11px" }}>
                                {item.name || item.description || "No title"}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              {isModified && (
                                <Badge color={isSelected ? "warning" : "warning"} pill style={{ fontSize: "10px" }}>
                                  Edited
                                </Badge>
                              )}
                              <Badge color={isSelected ? "light" : "secondary"} pill className={isSelected ? "text-primary" : ""}>
                                #{idx + 1}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Right Area: Interactive Editor for Selected Item */}
                <div className="col-md-8">
                  {activeItem ? (
                    <Card className="border shadow-2xs h-100">
                      <CardBody className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                          <div>
                            <span className="badge bg-primary-soft text-primary font-monospace px-2 py-1 mb-1">
                              {isGroups ? "Group Slug:" : "Product SKU:"} {activeItem.identifier}
                            </span>
                            <h6 className="fw-bold text-dark mb-0">{activeItem.name}</h6>
                          </div>
                          <Badge color="success" pill className="px-2 py-1 d-flex align-items-center gap-1">
                            <FiCheckCircle size={12} /> AI Generated
                          </Badge>
                        </div>

                        {/* Short Description */}
                        <div className="mb-3">
                          <label className="fw-bold small text-dark mb-1">
                            Short Description (Marketing Summary)
                          </label>
                          <Input
                            key={`desc_${activeItem.identifier}`}
                            type="text"
                            value={activeItem.description || ""}
                            onChange={(e) => handleUpdateItem(activeItem.identifier, "description", e.target.value)}
                            placeholder="Enter short description..."
                            className="bg-light"
                          />
                        </div>

                        {/* Long Description (Rich Overview) */}
                        <div className="mb-2">
                          <label className="fw-bold small text-dark mb-1">
                            Long Description (Overview & Features)
                          </label>
                          <ReactQuill
                            key={`quill_${activeItem.identifier}`}
                            theme="snow"
                            value={activeItem.long_description || ""}
                            onChange={(val) => handleUpdateItem(activeItem.identifier, "long_description", val)}
                            style={{ height: "200px", marginBottom: "45px" }}
                            className="bg-white rounded"
                          />
                        </div>
                      </CardBody>
                    </Card>
                  ) : (
                    <div className="p-5 text-center text-muted border rounded">
                      No item selected.
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Actions */}
              <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className="text-muted small">
                  Ready to apply <strong className="text-dark">{previewItems.length}</strong> humanized descriptions to database.
                </span>
                <GeneralButton
                  title={
                    applying
                      ? "Applying to Database..."
                      : `Apply All to Database (${previewItems.length} ${isGroups ? "Groups" : "Products"})`
                  }
                  className="blue-button mb-0"
                  width="320px"
                  icon={<FiCheckCircle size={16} />}
                  loading={applying}
                  disabled={applying || previewItems.length === 0}
                  handleClick={handleApplyPreviewToDB}
                />
              </div>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export const BulkHumanizeGroupsModal = (props) => <BulkHumanizeModal {...props} targetType="groups" />;
export const BulkHumanizeProductsModal = (props) => <BulkHumanizeModal {...props} targetType="products" />;

export default BulkHumanizeModal;
